/* eslint-disable @typescript-eslint/no-explicit-any */
declare global {
  interface Window {
    fbq: (...args: any[]) => void;
    _fbq: any;
  }
}

const DEFAULT_PIXEL_ID = '1614282913201177';
let activePixelId = DEFAULT_PIXEL_ID;
let initialized = false;

const loadScript = (pixelId: string): void => {
  if (initialized) return;
  initialized = true;
  const n: any = function (...args: any[]) {
    n.callMethod ? n.callMethod.apply(n, args) : n.queue.push(args);
  };
  if (!window._fbq) window._fbq = n;
  window.fbq = n;
  n.push = n;
  n.loaded = true;
  n.version = '2.0';
  n.queue = [];
  const t = document.createElement('script');
  t.async = true;
  t.src = 'https://connect.facebook.net/en_US/fbevents.js';
  const s = document.getElementsByTagName('script')[0];
  s.parentNode?.insertBefore(t, s);
  window.fbq('init', pixelId);
  window.fbq('track', 'PageView');
};

/** Called from App.tsx with dynamic pixel ID from config params */
export const initFacebookPixel = (pixelId?: number): void => {
  if (typeof window === 'undefined') return;
  if (pixelId) activePixelId = String(pixelId);
  loadScript(activePixelId);
};

/** Called from StartPageStep as fallback init with default pixel ID */
export const initFbPixel = (): void => {
  if (typeof window === 'undefined') return;
  loadScript(activePixelId);
};

const fbqCall = (...args: any[]): void => {
  if (typeof window !== 'undefined' && window.fbq) {
    try {
      window.fbq(...args);
    } catch (e) {
      console.warn('[fbPixel] fbq call failed', e);
    }
  }
};

// ────────────────────────────────────────────────────────────
// LEGACY EVENTS (used by mytraits.ai legacy flow — keep as-is)
// ────────────────────────────────────────────────────────────

export const fbPageView = (): void => fbqCall('track', 'PageView');

export const fbSubscribedButtonClick = (step: string): void =>
  fbqCall('trackCustom', 'SubscribedButtonClick', { step });

export const fbCompleteRegistration = (): void =>
  fbqCall('track', 'CompleteRegistration');

export const fbOnePurchase = (value: number, currency = 'USD'): void =>
  fbqCall('trackCustom', 'One-timePurchase', { value, currency });

export const fbPurchase = (value: number, currency = 'USD'): void =>
  fbqCall('track', 'Purchase', { value, currency });

// ────────────────────────────────────────────────────────────
// MYDNATEST24 EVENTS
// Standard events use `track`; custom events use `trackCustom`.
// All amounts default to USD currency.
// ────────────────────────────────────────────────────────────

/**
 * PageView with screen context. Fired on EVERY screen of the onboarding.
 *
 * Meta uses `PageView` as a standard event but we attach extra params so
 * we can build screen-level funnels in Events Manager.
 */
export const fbScreenView = (screenId: string): void =>
  fbqCall('track', 'PageView', { screen_id: screenId });

/**
 * Lead — fired when email is entered AND valid AND user clicks Continue.
 * Canonical "warm lead" event for ad optimization.
 */
export const fbLead = (email?: string): void =>
  fbqCall('track', 'Lead', email ? { email } : {});

/**
 * AddToCart — fired when user lands on paywall (SubscriptionStep mount)
 * with the chosen plan visible. Models intent before payment.
 *
 * value=1.00 is a placeholder optimization signal (Meta requires non-zero).
 * Real revenue is sent server-side via Purchase.
 */
export const fbAddToCart = (planId: string): void =>
  fbqCall('track', 'AddToCart', {
    content_ids: [planId],
    content_type: 'product',
    currency: 'USD',
    value: 1.00,
  });

/**
 * InitiateCheckout — fired when user clicks payment button (Apple Pay or
 * card). Sent BEFORE tokenization completes — captures payment intent.
 */
export const fbInitiateCheckout = (
  planId: string,
  method: 'apple_pay' | 'card' | 'paypal',
  transactionId?: string,
): void =>
  fbqCall('track', 'InitiateCheckout', {
    content_ids: [planId],
    content_type: 'product',
    currency: 'USD',
    value: 1.00,
    payment_method: method,
    ...(transactionId ? { transaction_id: transactionId } : {}),
  });

/**
 * ViewContent — fired when an upsell screen opens.
 *
 * @param contentName e.g. 'upsell_1_match_report'
 * @param value 27 / 47 / 67 (price of that upsell)
 */
export const fbViewContent = (contentName: string, value: number): void =>
  fbqCall('track', 'ViewContent', {
    content_name: contentName,
    content_type: 'product',
    currency: 'USD',
    value,
  });

/**
 * CompleteRegistration — fired when user reaches success screen after
 * subscription is created and confirmed.
 *
 * NOTE: Authoritative Purchase event is sent server-side from Sonara via
 * Conversions API after webhook confirmation. This browser event is for
 * Meta Pixel ad optimization only.
 */
export const fbCompleteRegistrationMydna = (): void =>
  fbqCall('track', 'CompleteRegistration');

/**
 * UpsellSkip (custom) — fired when user dismisses upsell without buying.
 */
export const fbUpsellSkip = (upsellName: string): void =>
  fbqCall('trackCustom', 'UpsellSkip', { upsell_name: upsellName });

/**
 * SubscribedButtonClick (custom) — already exists for legacy flow.
 * Re-exported for clarity in mydna usage.
 *
 * Used for CTA clicks WITHIN onboarding (social_proof, feature, summary,
 * paywall buttons).
 */
export const fbMydnaSubscribedButtonClick = (
  ctaLocation: string,
): void => fbqCall('trackCustom', 'SubscribedButtonClick', { step: ctaLocation });
